import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { MagnifyingGlass, X } from "@phosphor-icons/react"
import { HighlightedText } from "@/components/highlighted-text"
import { FormError } from "@/components/ui/form.tsx"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchContent, SearchItem, SuggestionSearch } from "@/components/ui/search"
import { useClickOutside } from "@/hooks/useClickOutside.ts"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { cn } from "@/lib/utils"
import {
  mapPointOfCareSpecialty,
  type PointOfCareSpecialtySource,
} from "@/modules/patients/detail/progress-note/lib/map-point-of-care-specialty"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam.ts"
import {
  CareProviderSpecialtyCH,
  usePointOfCareSpecialtyQuery,
} from "@/modules/patients/hooks/usePointOfCareSpecialtyQuery.ts"

const SPECIALTY_SELECT_INPUT_ID = "specialty-select"

type SpecialtyTranslationType = {
  id: string
  language: string
  word: string
  synonym: string | null
  specialty?: CareProviderSpecialtyCH
  betterDocKey?: string
}

interface SpecialtySelectProps {
  defaultValue: string
  onSelect: (item: SpecialtyTranslationType) => void
  onClear?: () => void
  error?: boolean
  required?: boolean
  disabled?: boolean
}

const SpecialtySelect = ({
  defaultValue,
  onSelect,
  onClear,
  error,
  required = true,
  disabled,
}: SpecialtySelectProps) => {
  const { t } = useTranslation()
  const language = useCurrentLanguage()
  const country = useCountryParam()
  const searchRef = useRef<HTMLInputElement>(null)
  const searchResultsRef = useRef<HTMLInputElement>(null)
  const [searchResultVisible, setSearchResultVisible] = useState(false)
  const [searchValue, setSearchValue] = useState(defaultValue)
  const { chPointOfCareSpecialties, dePointOfCareSpecialties, debounceSearch } = usePointOfCareSpecialtyQuery()

  const pointOfCareSpecialtyOptions = useMemo(() => {
    const pointOfCareSpecialties: ReadonlyArray<PointOfCareSpecialtySource> =
      (country === "CH" ? chPointOfCareSpecialties?.nodes : dePointOfCareSpecialties?.nodes) ?? []
    return pointOfCareSpecialties
      .map((pointOfCareSpecialty) => mapPointOfCareSpecialty(pointOfCareSpecialty, language))
      .toSorted((first, second) => first.word.toLowerCase().localeCompare(second.word.toLowerCase()))
  }, [country, chPointOfCareSpecialties, dePointOfCareSpecialties, language])

  useEffect(() => {
    setSearchValue(defaultValue)
  }, [defaultValue])
  const handleSearchInputChange = (value: string) => {
    debounceSearch(value)
    setSearchValue(value)
  }

  useClickOutside((event: MouseEvent) => {
    if (searchResultsRef.current && !searchResultsRef.current.contains(event.target as HTMLElement)) {
      closeSearchResults()
    }
  }, searchResultsRef)

  const handleSelect = (item: SpecialtyTranslationType) => {
    setSearchValue(item.word)
    onSelect(item)
    closeSearchResults()
    searchRef.current?.blur()
  }
  const handleClearSearch = () => {
    onClear?.()
    debounceSearch("")
    setSearchValue("")
    closeSearchResults()
  }
  const handleKeyUp = (event?: React.KeyboardEvent<HTMLInputElement>) => {
    if (!searchResultVisible) setSearchResultVisible(true)
    else if (event?.code === "Tab" && !event?.shiftKey) {
      setSearchResultVisible(false)
    }
  }

  const closeSearchResults = () => {
    setSearchResultVisible(false)
  }
  const renderSearchItem = (option: SpecialtyTranslationType) => (
    <SearchItem key={option?.id} onSelect={() => handleSelect(option)}>
      <div className="relative" role="option" aria-selected="false">
        <div className="grid px-2 py-2">
          <HighlightedText text={option.word} searchValue={searchValue} className="truncate" />
          <HighlightedText text={option.synonym ?? ""} searchValue={searchValue} className="truncate" />
        </div>
      </div>
    </SearchItem>
  )

  return (
    <SuggestionSearch className="col-span-4" label="Specialty">
      <label
        data-testid="specialty-select-label"
        htmlFor={SPECIALTY_SELECT_INPUT_ID}
        className="absolute top-6 right-1 -translate-y-1/2 p-2">
        <MagnifyingGlass className="text-on-surface-subdued" />
      </label>

      <input
        id={SPECIALTY_SELECT_INPUT_ID}
        ref={searchRef}
        value={searchValue}
        className={cn(
          "peer border-border bg-surface-subdued text-on-surface placeholder:text-on-surface-subdued hover:bg-surface-hovered focus:outline-primary active:bg-surface-pressed h-12 w-full rounded-md border pt-0 pr-23.75 pl-4 text-sm outline-offset-0 transition-all focus:outline-2 disabled:cursor-not-allowed disabled:opacity-50",
          error && "outline-critical focus:outline-critical outline-1",
        )}
        data-testid="specialty-select"
        placeholder={`${t("episodeOfCare.progressNote.pointOfCare.specialty")}${required ? "*" : ""}`}
        autoComplete="off"
        disabled={disabled}
        onChange={({ target }) => handleSearchInputChange(target.value)}
        onBlur={({ target }) => handleSearchInputChange(target.value)}
        onKeyUp={handleKeyUp}
        onKeyDown={handleKeyUp}
        onClick={() => handleKeyUp()}
      />
      {searchValue && (
        <button
          className="hover:bg-surface-hovered visible absolute top-6 right-14 -translate-y-1/2 cursor-pointer rounded-full p-1 transition-all"
          onClick={handleClearSearch}
          type="button">
          <X className="text-on-surface-subdued h-6 w-6 cursor-pointer" />
        </button>
      )}
      {error && <FormError>{t("common.fieldErrorList.requiredField")}</FormError>}
      {searchResultVisible && (
        <SearchContent data-testid="specialty-search-list" ref={searchResultsRef} className="overflow-y-auto">
          {pointOfCareSpecialtyOptions.length === 0 ? (
            <div className="text-on-surface-subdued max-h-64">{t("common.nothingFound")}</div>
          ) : (
            <ScrollArea viewportClassName="max-h-64">
              <div className="py-2">
                {pointOfCareSpecialtyOptions.map((option: SpecialtyTranslationType) => renderSearchItem(option))}
              </div>
            </ScrollArea>
          )}
        </SearchContent>
      )}
    </SuggestionSearch>
  )
}

export { SPECIALTY_SELECT_INPUT_ID, SpecialtySelect, type SpecialtyTranslationType as SpecialtyType }
